'use client';

import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlockPart, useCommerceSource, tx, PortalLink } from '@akademi/site-builder-runtime';
import { AdapterPlaceholder, BlockRoot, sharedStyles } from '@akademi/site-builder-blocks';
import {
  AccountField,
  buttonStyle,
  errorStyle,
  formStyle,
  leadStyle,
  linkRowStyle,
  noticeStyle,
  titleStyle,
  useAccountErrorText,
} from './accountShared';

const { sectionInner } = sharedStyles;

type Localized = string | Record<string, string>;

/**
 * "Forgot your password?" — asks for an email, and the base sends a link and a
 * code to choose a new one with.
 *
 * <p>The confirmation says "if there is an account for that address, we have
 * sent an email" whether or not there is one. Anything more definite would let
 * this form be used to find out who shops here, one address at a time.
 */
export interface ForgotPasswordFormProps {
  title?: Localized;
  intro?: Localized;
  signInHref?: string;
  /** Where a shopper types the code, for reading the email on another device. */
  resetHref?: string;
}

export function ForgotPasswordForm({
  title,
  intro,
  signInHref = '/sign-in',
  resetHref = '/reset-password',
}: ForgotPasswordFormProps) {
  const commerce = useCommerceSource();
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');
  const errorText = useAccountErrorText();

  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!commerce?.forgotPassword) {
    return (
      <BlockRoot as="section">
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="ForgotPasswordForm"
            adapterName="CommerceSource"
            reason={t('commerce.account.noAdapter')}
          />
        </div>
      </BlockRoot>
    );
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!commerce?.forgotPassword || busy) return;
    setBusy(true);
    setError(null);
    try {
      await commerce.forgotPassword(email.trim());
      setSent(true);
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 520 }}>
        <h1 {...part('title')} style={titleStyle}>
          {tx(title) || t('commerce.account.forgotTitle')}
        </h1>
        {sent ? (
          <>
            <p role="status" style={noticeStyle}>
              {t('commerce.account.forgotSent')}
            </p>
            <div style={linkRowStyle}>
              <PortalLink href={resetHref}>{t('commerce.account.enterResetCode')}</PortalLink>
            </div>
          </>
        ) : (
          <>
            <p {...part('intro')} style={leadStyle}>
              {tx(intro) || t('commerce.account.forgotIntro')}
            </p>
            <form onSubmit={(event) => void submit(event)} style={formStyle}>
              {error && (
                <p role="alert" style={errorStyle}>
                  {error}
                </p>
              )}
              <AccountField
                id="forgot-email"
                label={t('commerce.account.email')}
                value={email}
                onChange={setEmail}
                type="email"
                autoComplete="email"
                required
              />
              <button type="submit" disabled={busy} style={buttonStyle}>
                {busy ? t('commerce.account.sending') : t('commerce.account.sendLink')}
              </button>
            </form>
          </>
        )}
        <div style={linkRowStyle}>
          <PortalLink href={signInHref}>{t('commerce.account.backToSignIn')}</PortalLink>
        </div>
      </div>
    </BlockRoot>
  );
}
